import DropdownMenu from "@/ts/base/Components/DropdownMenu";
import Main from "@/ts/base/Main";
import debounce from "debounce";

export default class SiteHeader {

	protected main: Main;
	protected el: HTMLElement | null;
	protected isLayoutAdjustmentsEnabled: boolean;
	protected isTransitioning: boolean = false;
	protected logoOriginalSize: {
		width: number;
		height: number;
	};
	protected branding!: HTMLElement | null;
	protected mainNavigation!: HTMLElement | null;
	protected sideNavigation!: HTMLElement | null;
	protected sideNavHamburger!: HTMLElement | null;
	protected mobileNavigation!: HTMLElement | null;
	protected primaryMenu!: HTMLUListElement | null;
	protected secondaryMenu!: HTMLUListElement | null;
	protected searchFormEl!: HTMLElement | null;
	protected searchToggleEl!: HTMLElement | null;
	protected searchWrapper!: HTMLElement | null;
	protected searchInputEl!: HTMLInputElement | null;
	protected dropdown!: DropdownMenu;


	public isSearchActive = false;

	constructor(isLayoutAdjustmentsEnabled: boolean = true) {
		this.main = Main.Instance;
		this.el = document.getElementById("SiteHeader");
		this.isLayoutAdjustmentsEnabled = isLayoutAdjustmentsEnabled;
		this.logoOriginalSize = {
			width: -1,
			height: -1
		};

		if (!(this.el instanceof HTMLElement)) {
			return;
		}

		this.branding = this.el.querySelector(".branding");
		this.mainNavigation = this.el.querySelector("nav.header-main-navigation");
		this.sideNavigation = this.el.querySelector("nav.header-side-navigation");
		this.sideNavHamburger = this.el.querySelector(".side-navigation-toggle .hamburger");
		this.mobileNavigation = this.el.querySelector("nav.mobile-navigation");
		this.primaryMenu = this.el.querySelector("ul.primary-menu");
		this.secondaryMenu = this.el.querySelector("ul.secondary-menu");
		this.searchFormEl = this.el.querySelector(".search-form");

		if (this.mainNavigation) {
			this.dropdown = new DropdownMenu(this.mainNavigation, this.el);
		}

		window.addEventListener(Main.IsLoadedClass, () => {
			this.initSearchForm();
			this.createHandlers();
			this.createResizeObserver();
			this.createIntersectionObserver();
		});
	}

	protected createHandlers(): void {
		window.addEventListener('load', this.initSideNavigationToggle.bind(this));
		this.el?.addEventListener("transitionstart", this.onTransitioning.bind(this), {passive: true});
		this.el?.addEventListener("transitionend", this.onTransitioning.bind(this), {passive: true});
		this.el?.addEventListener("transitioncancel", this.onTransitioning.bind(this), {passive: true});
	}

	protected createIntersectionObserver(): void {
		const observer = new IntersectionObserver(this.onIntersectionObserve.bind(this), {
			threshold: 1
		});
		observer.observe(document.getElementById("skip") as HTMLElement);
	}

	protected createResizeObserver(): void {
		if (this.isLayoutAdjustmentsEnabled && this.el) {
			if (typeof window.ResizeObserver === "function") {
				const observer = new window.ResizeObserver(this.onResizeObserve.bind(this));
				observer.observe(this.el, {
					box: "border-box"
				});

			}
			else {
				window.addEventListener("resize", debounce(this.adjustComponentSizes.bind(this), 150));
			}

			this.adjustComponentSizes();
		}
	}

	isFixedTransparent(): boolean {
		return window.document.body.classList.contains("use-transparent-header");
	}

	getElement(): HTMLElement | null {
		return this.el;
	}

	getHeight(): number {
		return this.el?.clientHeight || 0;
	}

	getComputedStyle(): CSSStyleDeclaration | null {
		if (this.el) {
			return window.getComputedStyle(this.el);
		}

		return null;
	}

	adjustComponentSizes(): void {
		if (!this.el || !this.branding || !this.mainNavigation || !this.mobileNavigation || !this.primaryMenu) {
			return;
		}

		let percent = 0;
		if (this.logoOriginalSize.width === -1) {
			this.logoOriginalSize.width = parseInt(this.branding.style.maxWidth);
			this.logoOriginalSize.height = parseInt(this.branding.style.maxHeight);
		}

		this.branding.style.flexBasis = "";
		this.branding.style.maxWidth = "";
		this.mainNavigation.style.flexBasis = "";
		this.mainNavigation.style.maxWidth = "";
		this.mobileNavigation.style.flexBasis = "";
		this.mobileNavigation.style.maxWidth = "";
		let wHeader = this.el.clientWidth;
		let wBranding = this.branding.clientWidth;
		let wMainNavigation = this.mainNavigation.clientWidth;
		let wMobileNavigation = this.mobileNavigation.clientWidth;
		let styleMobileNavigation = window.getComputedStyle(this.mobileNavigation);
		if (styleMobileNavigation.display !== "none") {
			percent = Math.floor(wMobileNavigation / wHeader * 100)
			this.mobileNavigation.style.flexBasis = percent + "%";
			this.mobileNavigation.style.maxWidth = percent + "%";
			this.branding.style.flexBasis = 100 - percent + "%";
			this.branding.style.maxWidth = 100 - percent + "%";
		}
		else {
			if (wBranding + wMainNavigation > wHeader) {
				let wPrimaryMenu = this.primaryMenu.clientWidth + 40;
				percent = Math.floor(wPrimaryMenu / wHeader * 100);
				this.branding.style.flexBasis = 100 - percent + "%";
				this.branding.style.maxWidth = 100 - percent + "%";
				this.mainNavigation.style.flexBasis = percent + "%";
				this.mainNavigation.style.maxWidth = percent + "%";
			}
			else {
				this.branding.style.flexBasis = this.logoOriginalSize.width + "px";
				this.branding.style.maxWidth = this.logoOriginalSize.width + "px";
				this.branding.style.maxHeight = this.logoOriginalSize.height + "px";
				this.mainNavigation.style.flexBasis = "";
				this.mainNavigation.style.maxWidth = "";
			}
		}
	}

	protected initSearchForm(): void {
		if (this.el && this.searchFormEl) {
			this.searchToggleEl = this.el.querySelector(".search-toggle");
			this.searchWrapper = this.el.querySelector(".search-form-wrapper");
			this.searchInputEl = this.searchFormEl.querySelector("input");
			if (this.searchToggleEl) {
				this.searchToggleEl.addEventListener("click", this.onSearchToggleClick.bind(this));
			}
		}
	}

	protected initSideNavigationToggle(): void {
		if (this.sideNavigation && this.sideNavHamburger) {
			this.sideNavHamburger.addEventListener("click", (e) => {
				e.preventDefault();
				if (this.sideNavHamburger?.classList.contains("active")) {
					this.sideNavigation?.classList.remove("active");
					this.sideNavHamburger?.classList.remove("active");
				}
				else {
					this.sideNavigation?.classList.add("active");
					this.sideNavHamburger?.classList.add("active");
				}
			});
		}
	}

	protected updateClassName(isScrolled: boolean): void {
		if (isScrolled) {
			window.document.body.classList.add("scrolled");
		}
		else {
			window.document.body.classList.remove("scrolled");
		}
	}

	protected onSearchToggleClick(e: Event) {
		this.isSearchActive = !this.isSearchActive;

		this.el?.classList.toggle("search-active");
		this.searchWrapper?.classList.toggle("active");
	}

	protected onIntersectionObserve(entries: IntersectionObserverEntry[], observer: IntersectionObserver): void {
		if (this.isTransitioning) {
			return;
		}

		if (!entries[0].isIntersecting) {
			this.updateClassName(true);
		}
		else {
			this.updateClassName(false);
		}
	}

	protected onResizeObserve(entries: ResizeObserverEntry[], observer: ResizeObserver): void {
		this.adjustComponentSizes();
	}

	protected onSearchCancelClick(e: Event) {
		this.el?.classList.remove("search-active");
		this.isSearchActive = false;
	}

	protected onTransitioning(e: Event): void {
		this.isTransitioning = e.type == "transitionstart";
	}
}
